<template>
  <v-container
    fluid
    class="pa-0"
  >
    <v-row>
      <v-col cols="12">
        <div class="primary--text text-h5 font-weight-bold my-4 title-bar">
          {{ $t('employee.change_viewing_right_title') }}
        </div>
      </v-col>
    </v-row>
    <v-row v-if="errorState">
      <v-col cols="12">
        <v-alert
          v-model="errorState"
          dense
          type="error"
          close-icon="mdi-close-circle-outline"
          :dismissible="true"
        >
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row align="stretch">
      <v-col
        cols="12"
        sm="6"
        xl="4"
      >
        <user-information
          :employee="employee"
          :remaining-view="companyRemainingView"
          :loading="loading"
          @toggled="onEmployeeToggled"
        />
      </v-col>
      <v-col
        cols="12"
        sm="6"
        xl="4"
      >
        <company-info
          :company="companyRelated"
          :loading="loading"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card style="border-width: 0">
          <v-card-title class="primary--text">
            {{ $t('detail.parent_company_program') }}
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              :loading="loading"
              :headers="headers"
              :items="items"
              :items-per-page="10"
              class="elevation-1"
              :hide-default-footer="items.length < 10"
            >
              <template #[`item.view`]="{item}">
                <span>{{ item.view || 'N/A' }}{{ $i18n.locale === 'jp' ? '名' : '' }}</span>
              </template>
              <template #[`item.available`]="{item}">
                <span>{{ item.view ? item.view - item.active_view : 'N/A' }}{{ $i18n.locale === 'jp' ? '名' : '' }}</span>
              </template>
              <template #[`item.action`]="{item, index}">
                <v-btn
                  rounded
                  outlined
                  color="success"
                  :disabled="item.action || !item.view || item.view - item.active_view === 0"
                  :loading="item.loading"
                  small
                  @click="showAlert(item, index)"
                >
                  {{ item.action ? $t('employee.active') : $t('employee.enable') }}
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog
      v-model="enableProgramAlert"
      persistent
      :width="$vuetify.breakpoint.mdAndUp ? '40%' : '80%'"
    >
      <v-card>
        <v-card-title />
        <v-card-text
          class="pb-0"
        >
          <v-alert
            type="warning"
            dense
            outlined
          >
            {{ $t('detail.warning') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            rounded
            outlined
            color="error"
            @click="enableProgramAlert = false"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            rounded
            outlined
            color="success"
            @click="onToggleProgramClick(programItem, programIndex)"
          >
            {{ $t('common.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import UserInformation from '../../../components/UserInformation'
import CompanyInfo from '../../../components/CompanyInfo'
import QUATSError from '../../../plugins/error'
export default {
  components: {CompanyInfo, UserInformation},
  data () {
    return {
      enableProgramAlert: false,
      loading: false,
      employee: {},
      companyRelated: {},
      items: [],
      toggleResult: null,
      allPrograms: [],
      error: null,
      errorState: false,
      programItem: {},
      programIndex: 0,
      companyRemainingView: 0,
    }
  },
  computed: {
    /**
     * @desc To compute header for the program information that can be assigned to the employee
     * */
    headers () {
      return [
        { text: 'ID', align: 'center', divider: true, value: 'id', width: '30px', class: 'primary white--text' },
        { text: this.$t('detail.name'), align: 'center', divider: true, value: 'title', width: '100px', class: 'primary white--text' },
        { text: this.$t('detail.max_view'), align: 'center', divider: true, value: 'view', width: '100px', class: 'primary white--text' },
        { text: this.$t('detail.available'), align: 'center', divider: true, value: 'available', sortable: false, width: '100px', class: 'primary white--text' },
        { text: this.$t('detail.can_view'), align: 'center', divider: true, value: 'action', sortable: false, width: '100px', class: 'primary white--text' },
      ]
    },
  },
  /**
   * @desc call on init function to kick off data query process
   * */
  mounted() {
    this.onPageRefresh()
  },
  methods: {
    /***
     * @desc helper function to call all API together at once when the page freshly start
     */
    onPageRefresh() {
      this.loading = true
      const customerId = this.$router.currentRoute.params.id
      const param = `?match_customer_id=${customerId}`
      const promises = [
        window.axios.get('/api/employee-detail/parent-company'),
        window.axios.get('/api/employee-detail/corporate-account/quota'),
        window.axios.get(`/api/employee-detail/employees${param}`),
        window.axios.get('/api/employee-detail/quats-programs'),
        window.axios.get('/api/employee-detail/quats-programs/quota')
      ]
      Promise.all(promises)
        .then(res => {
          this.getParentCompany(res[0].data.data.data, res[1].data.data.data)
          this.getUserInfo(res[2].data.data.data.data)
          this.checkUserProgram(this.filterEmployeeProgram(res[3].data.data.data, res[4].data.data.data.current))
          this.errorState = false
        }).catch(err => {
          this.errorState = true
          this.error = new QUATSError(this.$i18n, err).message
        }).finally(() => {
          this.loading = false
        })
    },

    /**
     * @desc to get Parent Company information and corporate account information
     * */
    getParentCompany(parentCompany, corporateAccount) {
      this.companyRelated = Object.assign({}, {
        id: parentCompany.id,
        name: parentCompany.name,
        name_kana: parentCompany.name_kana,
        max: corporateAccount.total,
        active: corporateAccount.active
      })
      this.companyRemainingView = this.companyRelated.max - this.companyRelated.active
    },

    /**
     * @desc to get employee information by using exact filter of employee list api
     * */
    getUserInfo(data) {
      this.employee = Object.assign({}, {
        id: data[0].customer_id,
        name: data[0].name,
        active: data[0].active_flg,
        delete: data[0].delete_flg,
        group_permission: data[0].program_quats_permissions.group_ids
      })
    },

    /**
     * @desc filter employee with program
     * */
    filterEmployeeProgram(allPrograms, programQuats) {
      const programList = []
      programQuats.forEach(x => {
        allPrograms.forEach(y => {
          if (x.group_id === y.groupId) {
            x.audioOnly = y.audioOnly
            x.id = y.id
            x.programObj = y.programObj
            x.title = y.title
            x.groupId = x.group_id
            delete x.group_id
            x.view = x.total_views
            delete x.total_views
            x.active_view = x.active_views
            delete x.active_views
          }
        })
        programList.push(x)
      })
      return programList
    },

    /**
     * @desc check if user has the programs enabled
     * */
    checkUserProgram(data) {
      const items = []
      data.forEach(x => {
        let action = false
        if (this.employee?.group_permission?.includes(x.groupId)) {
          action = true
        }
        items.push({
          action: action,
          loading: false,
          ...x
        })
      })
      this.items = items
      this.loading = false
    },

    /**
     * @desc handle employee corporate account toggle
     * */
    onEmployeeToggled(newStatus) {
      const requestBody = {
        'customer_id': this.employee.id,
        'active': newStatus
      }
      window.axios.patch('/api/employee-detail/corporate-account/employee/toggle', requestBody)
        .then(res => {
          this.toggleResult = res
          if (newStatus === true) {
            this.$store.commit('snackbar/successSnackbar', [this.$t('employee.corporate_activated')])
          } else {
            this.$store.commit('snackbar/successSnackbar', [this.$t('employee.corporate_deactivated')])
          }
          this.onPageRefresh()
          this.errorState = false
        }).catch(err => {
          this.error = new QUATSError(this.$i18n, err).message
          this.errorState = true
        }).finally(() => {
          this.$store.commit('snackbar/activateSnackbar', true)
        })
    },

    /**
     * function to show the alert message for to verify enabling the user to view program
     * @param item information of program need to assign to
     * @param index index of item in array
     */
    showAlert(item, index) {
      this.programItem = item
      this.programIndex = index
      this.enableProgramAlert = true
    },

    /**
     * @desc to handle when a program is enabled for the customer
     * */
    onToggleProgramClick(obj, index) {
      this.items[index].loading = true
      const data = {
        customer_id: this.employee.id,
        itc_group_id: obj.groupId
      }
      window.axios.patch('/api/employee-detail/quats-programs/employees/enable', data)
        .then(() => {
          this.onPageRefresh()
          this.errorState = false
        }).catch(err => {
          this.error = new QUATSError(this.$i18n, err).message
          this.errorState = true
        }).finally(() => {
          this.items[index].loading = false
          this.enableProgramAlert = false
        })
    },
  }
}
</script>

<style scoped>
::v-deep .theme--light.v-data-table .v-data-table-header th.sortable .v-data-table-header__icon {
  color: white;
}
</style>
